import React, { useCallback, useEffect, memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import { updateConfigHistorySavedChartDataConfigs } from '../../../redux/actions';
import { DeviceChart, DeviceChartEditableTitle, getCounter, getDefaultTitle } from '../../shared/DeviceChart';
import { KPIFieldset } from './Form';
import styles from './ConfigHistoryChart.module.css';

const ConfigHistoryChart = memo(function ConfigHistoryChart({ deviceId, height }) {
  const dispatch = useDispatch();
  const deviceCounters = useSelector((state) => selectDeviceConfigHistory(state, deviceId)?.localCountersData ?? []);
  const selectedCounters = useSelector((state) => selectDeviceConfigHistory(state, deviceId)?.selectedCounters);
  const storedValues = useSelector((state) => selectDeviceConfigHistory(state, deviceId)?.savedChartDataConfigs);
  const defaultValues = useMemo(
    () => ({
      name: '',
      state: {
        query: {
          devices: [deviceId],
          counters: [],
          actions: []
        },
        datasets: [],
        scales: [
          {
            key: 'x',
            label: '',
            position: 'bottom'
          }
        ]
      }
    }),
    [deviceId]
  );
  const onSubmit = useCallback(
    (values) => {
      dispatch(updateConfigHistorySavedChartDataConfigs(deviceId, values));
    },
    [dispatch, deviceId]
  );
  const methods = useForm({
    defaultValues,
    values: storedValues
  });
  const { handleSubmit, control } = methods;
  const { fields: counters, replace: replaceCounters } = useFieldArray({
    name: 'state.query.counters',
    control
  });

  useEffect(() => {
    return () => {
      // store values on unmount
      handleSubmit(onSubmit)();
    };
  }, [handleSubmit, onSubmit]);

  useEffect(() => {
    if (!selectedCounters) {
      return;
    }

    replaceCounters(selectedCounters.slice(0, 3).map(({ name }) => getCounter({ name })));
  }, [replaceCounters, selectedCounters]);

  return (
    <FormProvider {...methods}>
      <div className={styles.root}>
        <div className={styles.container}>
          <div className={styles.title}>
            <DeviceChartEditableTitle
              variant="history"
              editable={selectedCounters != null && selectedCounters.length > 0}
              defaultValue={getDefaultTitle(counters)}
            />
          </div>
          <div className={styles.chart}>
            <DeviceChart.Preview counters={deviceCounters} height={height} />
          </div>
        </div>
        <div className={styles.separator} />
        <div className={styles.controls}>
          <div className={styles.counters}>
            {counters.map((counter, index) => (
              <KPIFieldset
                key={counter.id}
                name={`state.query.counters.${index}`}
                value={counter}
                options={selectedCounters}
              />
            ))}
          </div>
        </div>
      </div>
    </FormProvider>
  );
});

export default ConfigHistoryChart;

function selectDeviceConfigHistory(state, deviceId) {
  return state.ui.singleDevicePage.configHistory[deviceId];
}
